import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { normalizeSlug, validateSlug } from '../lib/slugify.js';

import RelativeTime from '../components/shared/RelativeTime.jsx';
function ConfirmDeleteDialog({ orgName, onConfirm, onCancel, isPending }) {
  const [typed, setTyped] = useState('');
  // vista#660: require the fixed keyword `delete`, not the org name (consistent
  // with the main dashboard's destructive confirmations).
  const matches = typed === 'delete';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">Delete Organization</h3>
        <p className="mt-2 text-sm text-gray-600">
          This will permanently remove <span className="font-semibold">{orgName}</span> and all its users, agents, and data.
        </p>
        {isPending ? (
          /* vista#660: the cascade can take a while — show an indeterminate
             progress bar + status so the modal doesn't look hung. */
          <div className="mt-4">
            <p className="text-sm text-gray-700">Deleting organization… this can take a moment.</p>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div className="h-full w-1/3 animate-pulse rounded-full bg-red-500" style={{ animation: 'indeterminate 1.2s ease-in-out infinite' }} />
            </div>
            <style>{`@keyframes indeterminate{0%{margin-left:-35%}100%{margin-left:100%}}`}</style>
          </div>
        ) : (
          <>
            <p className="mt-3 text-sm text-gray-600">
              Type <span className="font-mono font-semibold text-red-600">delete</span> to confirm:
            </p>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="mt-2 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
              placeholder="delete"
              autoFocus
            />
          </>
        )}
        <div className="mt-4 flex justify-end gap-3">
          <button onClick={onCancel} disabled={isPending} className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!matches || isPending}
            className="rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isPending ? 'Deleting…' : 'Delete Organization'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateOrgModal({ onClose, onSubmit, isPending, error }) {
  const [name, setName] = useState('');
  // Slug live-derives from name until the operator types in the slug
  // field — once they do, slugTouched flips and we stop auto-overwriting.
  // This matches the standard "edit the suggestion" pattern operators
  // already expect from cloud consoles.
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const derivedSlug = slugTouched ? slug : normalizeSlug(name);
  const slugError = derivedSlug ? validateSlug(derivedSlug) : null;

  function handleNameChange(value) {
    setName(value);
    if (!slugTouched) setSlug(normalizeSlug(value));
  }

  function handleSlugChange(value) {
    setSlugTouched(true);
    setSlug(value.toLowerCase());
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">Create Organization</h3>
        <form
          onSubmit={(e) => { e.preventDefault(); onSubmit({ name, slug: derivedSlug }); }}
          className="mt-4 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700">Organization Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Acme Communications"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Slug
              {!slugTouched && <span className="ml-1 text-xs font-normal text-gray-500">(auto from name — edit to override)</span>}
            </label>
            <input
              type="text"
              value={derivedSlug}
              onChange={(e) => handleSlugChange(e.target.value)}
              required
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 font-mono text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="acme-communications"
            />
            <p className="mt-1 text-xs text-gray-500">
              URL identifier — becomes <code className="text-gray-700">{derivedSlug || 'slug'}.vista-cloud.io</code>. Lowercase, hyphens only.
            </p>
            {slugError && <p className="mt-1 text-xs text-red-600">{slugError}</p>}
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || Boolean(slugError) || !derivedSlug}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Organizations() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [deletingOrg, setDeletingOrg] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.get('/vista-admin/organizations'),
  });

  const createMutation = useMutation({
    mutationFn: (body) => api.post('/vista-admin/organizations', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setShowCreate(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (orgId) => api.delete(`/vista-admin/organizations/${orgId}`, { confirm: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setDeletingOrg(null);
    },
  });

  const orgs = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organizations</h1>
          {!isLoading && <p className="mt-1 text-sm text-gray-500">{orgs.length} organization{orgs.length !== 1 ? 's' : ''}</p>}
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Create Organization
        </button>
      </div>

      {showCreate && (
        <CreateOrgModal
          onClose={() => { setShowCreate(false); createMutation.reset(); }}
          onSubmit={(body) => createMutation.mutate(body)}
          isPending={createMutation.isPending}
          error={createMutation.isError ? (createMutation.error?.body?.error || createMutation.error?.message) : null}
        />
      )}

      {deletingOrg && (
        <ConfirmDeleteDialog
          orgName={deletingOrg.name}
          onConfirm={() => deleteMutation.mutate(deletingOrg.org_id)}
          onCancel={() => setDeletingOrg(null)}
          isPending={deleteMutation.isPending}
        />
      )}

      {isLoading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          Loading organizations...
        </div>
      ) : orgs.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
          <div className="text-4xl">⊞</div>
          <h2 className="mt-4 text-lg font-semibold text-gray-900">No organizations yet</h2>
          <p className="mt-2 text-sm text-gray-500">Create your first ISP organization to get started.</p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Create Organization
          </button>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Users</th>
                <th className="px-4 py-3">Agents</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orgs.map((org) => (
                <tr
                  key={org.org_id}
                  onClick={() => navigate(`/orgs/${org.org_id}`)}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{org.name}</div>
                    <div className="text-xs text-gray-400">{org.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{org.user_count ?? 0}</td>
                  <td className="px-4 py-3 text-gray-600">{org.agent_count ?? 0}</td>
                  <td className="px-4 py-3 text-gray-500"><RelativeTime ts={org.created_at} /></td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeletingOrg(org); }}
                      className="rounded-md border border-red-300 px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteMutation.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {deleteMutation.error?.body?.error || deleteMutation.error?.message || 'Failed to delete organization'}
        </div>
      )}
    </div>
  );
}
